import { SyntheticEvent, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from 'app/hooks';
import { FormMsg, NoFormMsg, doFormMsg } from 'components/forms/FormMessage';
import { ModalDialog } from 'components/modal/Modal';
import { IMenu } from 'features/menu/menuAPI';
import { patchMenuAsync, postMenuAsync } from 'features/menu/menuSlice';

type MenuFormProps = {
  menu?: IMenu;
};

export default function MenuForm(props: MenuFormProps) {
  const { menu } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<IMenu>({ locale: 'nl' } as IMenu);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);

  useEffect(() => {
    if (menu) {
      setData(menu);
    }
  }, [menu]);

  const handleChangeData = (e: SyntheticEvent) => {
    doFormMsg(NoFormMsg, setFormMsg);
    const target = e.target as HTMLInputElement;
    setData({ ...data, [target.name]: target.value });
  };

  async function handleSubmit(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!menu) {
          // creating new menu
          dispatch(
            postMenuAsync({
              data: { ...data, items: [] } as IMenu,
            }),
          ).then(async (response: { payload: any }) => {
            switch (response.payload?.response.status) {
              case 200:
              case 201:
                doFormMsg(
                  {
                    message: intl.formatMessage({
                      id: 'X.SUCCESS',
                    }),
                    success: true,
                    timeout: 5000,
                  },
                  setFormMsg,
                );
                resolve(true);
                break;
              case 500:
              default:
                doFormMsg(
                  {
                    message: intl.formatMessage({ id: 'X.FAILED' }),
                    success: false,
                  },
                  setFormMsg,
                );
                reject(intl.formatMessage({ id: 'X.FAILED' }));
                break;
            }
          });
        } else {
          // updating existing menu
          dispatch(patchMenuAsync({ data })).then(
            async (response: { payload: any }) => {
              switch (response.payload?.response.status) {
                case 200:
                  doFormMsg(
                    {
                      message: intl.formatMessage({
                        id: 'X.SUCCESS',
                      }),
                      success: true,
                      timeout: 5000,
                    },
                    setFormMsg,
                  );
                  resolve(true);
                  break;
                case 500:
                default:
                  doFormMsg(
                    {
                      message: intl.formatMessage({ id: 'X.FAILED' }),
                      success: false,
                    },
                    setFormMsg,
                  );
                  reject(intl.formatMessage({ id: 'X.FAILED' }));
                  break;
              }
            },
          );
        }
      } catch (err) {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'X.FAILED' }),
            success: false,
          },
          setFormMsg,
        );
        reject(intl.formatMessage({ id: 'X.FAILED' }));
      }
    });
  }

  return (
    <ModalDialog
      title={<FormattedMessage id={menu ? 'MENUS.EDIT' : 'MENUS.FORM.NEW'} />}
      okBtn={{
        label: intl.formatMessage({ id: 'X.SAVE' }),
        onClick: handleSubmit,
      }}
      cancelBtn={{}}
      editing={true}
      enableEscape
      formMsg={formMsg}
    >
      <Form.Group as={Row}>
        <Col sm={12} md={12} lg={12}>
          <Form.Label htmlFor="MenuTitleInput">
            <FormattedMessage id="MENUS.FORM.TITLE_LABEL" />
          </Form.Label>
          <Form.Control
            id="MenuTitleInput"
            type="text"
            name="title"
            value={data.title || ''}
            onChange={handleChangeData}
            placeholder={intl.formatMessage({ id: 'MENUS.FORM.TITLE_PLCH' })}
            required
            minLength={3}
          />
          <FormattedMessage id="MENUS.FORM.MAINMENU" />
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Col sm={12} md={12} lg={12}>
          <Form.Label htmlFor="MenuLocaleInput">
            <FormattedMessage id="GENERAL.LOCALE.LABEL" />
          </Form.Label>
          <Form.Select
            id="MenuLocaleInput"
            name="locale"
            value={data.locale || ''}
            onChange={handleChangeData}
            required
          >
            <option value="nl">
              {intl.formatMessage({ id: 'GENERAL.LOCALE.NL' })}
            </option>
            <option value="en">
              {intl.formatMessage({ id: 'GENERAL.LOCALE.EN' })}
            </option>
          </Form.Select>
        </Col>
      </Form.Group>
    </ModalDialog>
  );
}
